// src/hooks/buildGhlWebhookPayload.ts
import { buildBuddyTags } from "@/lib/lead/tags";
import type { ExtractedLeadV2 } from "@/types/lead";
import type { Location } from "@/types/location";
import type { GhlSendLead, Salesperson } from "./ghlSenderTypes";
import {
  extractOpenFollowUpsForPayload,
  nextOpenFollowUpDueDate,
} from "./ghlSenderFollowUpShape";

type BuildCtx = {
  location: Location | null;
  salesperson: Salesperson | null;
  version: string;
  appDisplayName?: string;
  payloadLocation?: Location | null;
};

function extractedToRecord(ex: ExtractedLeadV2): Record<string, unknown> {
  return { ...ex } as Record<string, unknown>;
}

export function buildGhlWebhookPayload(lead: GhlSendLead, ctx: BuildCtx): Record<string, unknown> | null {
  const ex = lead.extracted;
  if (!ex) return null;
  const loc = ctx.payloadLocation ?? ctx.location;
  const payload: Record<string, unknown> = {
    ...extractedToRecord(ex),
    bb_tags: buildBuddyTags(ex, loc, ctx.version, ctx.appDisplayName),
    bb_version: ctx.version,
    bb_lead_id: lead.id,
    origin_location_id: lead.origin_location_id ?? null,
    origin_location_name: lead.origin_location_name ?? null,
  };
  const sp = ctx.salesperson;
  if (sp) {
    payload.bb_salesperson_name = sp.name;
    payload.bb_salesperson_cell = sp.cell;
    payload.bb_salesperson_email = sp.email;
  }
  if (loc?.id) payload.bb_location_id = loc.id;
  if (loc?.name) payload.bb_location_name = loc.name;
  if (loc?.address) payload.bb_location_address = loc.address;
  if (loc?.phone) payload.bb_location_phone = loc.phone;

  // v1.0 gate item 8: embed open follow-ups ("customer events") into the main
  // inbound-webhook payload so a GHL workflow can branch on them before the
  // per-follow-up `create_followup_task` POSTs land (see
  // `ghlSenderFollowUps.ts`). Additive — existing workflows that key off the
  // separate task POSTs keep working unchanged. When there are no open
  // follow-ups we omit the array + count so payloads stay clean for legacy
  // GHL workflows that only check for the presence of `bb_follow_ups`.
  const openFollowUps = extractOpenFollowUpsForPayload(lead.follow_ups);
  if (openFollowUps.length) {
    payload.bb_follow_ups = openFollowUps;
    payload.bb_open_followup_count = openFollowUps.length;
    const nextDue = nextOpenFollowUpDueDate(openFollowUps);
    if (nextDue) payload.bb_next_followup_at = nextDue;
  }

  return payload;
}
